
import React, { useState } from 'react';
import {
  OrganisationDto,
  UserDto,
} from 'models/dtoTypes';
import { CompanyListAttributes, UserAttributes } from 'kosmos/models';
import axios from 'axios';
import { get } from 'lodash';
import { date2str } from 'utils/date';
import {
  GetPaginatedAuditLogs,
  DisableOrganisation,
  EnableOrganisation,
  GetListScoredCompanies,
  GetAllAuditLogs,
} from 'services/openapiClient';
import {
  GetUsers,
  CreateUser,
  DisableUser,
  EnableUser,
} from 'services/user';
import { styled } from '@mui/material/styles';
import ResourceDetailContainer, { ITab } from 'kosmos/components/ResourceDetailContainer';
import ResourceTable from 'kosmos/components/ResourceTable';
import ResourceFullListTable from 'kosmos/components/ResourceFullListTable';
import Notice from 'kosmos/components/Notice';
import {
  GetOrganisation,
  UpdateOrganisation,
  GetAuditLogSummary,
} from 'services/openapiClient';
import generateNonce from 'utils/generateNonce';
import CreateResourceModal from 'kosmos/dialogs/CreateResourceModal';
import CreditUpdateWidget from './_creditUpdateWidget';
import CurrentUser from 'auth/currentUser';
import { auditLogFilterSchema, companyListCreateSchema, generateActivityColumns, generateAuditLogSummaryColumns, generateCompanyListColumns, generateScoredCompaniesColumns, generateUserColumns, scoredCompaniesFilterSchema, userCreateSchema } from './_organisationSchema';
import { CreateCompanyList, GetCompanyLists } from 'services/companyList';
import { EFeature } from 'auth/Authentication';
import ScoredCompaniesResourceTable from './_scoredCompaniesTable';
import { getZoneFromCountryCode } from 'utils/countries';

const PREFIX = 'OrganisationDetailPage';

const classes = {
  root: `${PREFIX}-root`,
  pageHeader: `${PREFIX}-pageHeader`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`& .${classes.pageHeader}`]: {
    width: '100%',
    position: 'relative',

    '& > button': {
      position: 'absolute',
      right: 0,
      top: 0,
    }
  },
}));

interface INoticeData {
  message: string;
  severity: 'error' | 'success';
}

interface IFilters {
  [key: string]: string;
}

function OrganisationDetailPage(props: { id: string }): JSX.Element {
  const id = props.id;
  const [openCreateUser, setOpenCreateUser] = React.useState(false);
  const [openCreateCompanyList, setOpenCreateCompanyList] = React.useState(false);
  const [openNotice, setOpenNotice] = React.useState(false);
  const [tabId, setTabId] = React.useState('details');
  const [nonce, setNonce] = React.useState('');
  const [noticeData, setNoticeData] = React.useState<INoticeData>();
  const [auditFilters, setAuditFilters] = useState<IFilters>({});

  const handleDownloadBillingReport = async () => {
    const filters = [
      { column: 'organisationId', value: id },
      { column: 'logType', value: 'billing' },
    ];
    if (auditFilters.createdMonth) {
      filters.push({
        column: 'createdMonth',
        value: auditFilters.createdMonth,
      });
    }

    const auditLogs = await GetAllAuditLogs({
      filters,
      sortColumn: 'created',
      isDesc: false,
    });

    const csvHeaders = 'Action name, Timestamp, Company Name, Country, Zone\n';
    const csvRows: string[] = auditLogs.map((item) => {
      let meta = {};
      try {
        meta = JSON.parse(item.metadata || '');
      } catch (e) {
        // ignore if meta has no data
      }
      let companyName: string = get(meta, ['CompanyName']) || '';
      if (companyName.includes('"')) {
        companyName = companyName.replace(/"/g, '\'');
      }
      companyName = `"${companyName}"`; // wrap name with "
      const companyCountry = get(meta, ['Country']) || '';
      const zone = getZoneFromCountryCode(companyCountry);
      const timestamp = item.created ? new Date(item.created).toISOString() : '';
      return [item.actionName, timestamp, companyName, companyCountry, zone].join(',');
    });

    const csvContent = `data:text/csv;charset=utf-8,${csvHeaders}${csvRows.join('\n')}`;

    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csvContent));
    link.setAttribute('download', `org_${id}${auditFilters.createdMonth ? `_${auditFilters.createdMonth}` : ''}.csv`);
    document.body.appendChild(link); // Required for FF

    link.click();
    link.remove();
  };

  const handleCloseCreatedNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenNotice(false);
  };

  const handleDisableOrganisation = async () => {
    if (!id) return;
    try {
      await DisableOrganisation(id);
      setNonce(generateNonce());
    } catch (e) {
      handleError(e);
    }
  };

  const handleEnableOrganisation = async () => {
    if (!id) return;
    try {
      await EnableOrganisation(id);
      setNonce(generateNonce());
    } catch (e) {
      handleError(e);
    }
  };

  const handleDisableUser = async (user: UserDto) => {
    try {
      return await DisableUser(user.id);
    } catch (e) {
      console.error(e);
      setNoticeData({
        message: 'Cannot disable this user',
        severity: 'error',
      });
    }
  };

  const handleEnableUser = async (user: UserDto) => {
    try {
      return await EnableUser(user.id);
    } catch (e) {
      console.error(e);
      setNoticeData({
        message: 'Cannot enable this user',
        severity: 'error',
      });
    }
  };

  const generateTabs = (): ITab<OrganisationDto>[] => [
    {
      id: 'details',
      title: 'Details',
      getUiSchema: () => ({
        fields: [
          {
            label: 'Id',
            name: 'id',
            readonly: true,
            widget: 'text',
          },
          {
            label: 'Status',
            name: 'status',
            readonly: true,
            widget: 'text'
          },
          {
            label: 'Name',
            name: 'name',
            widget: 'text'
          },
          {
            hint: 'Organisation name alias',
            label: 'Display Name',
            name: 'displayName',
            widget: 'text'
          },
          {
            label: 'Has Unlimited Credits',
            name: 'hasUnlimitedCredits',
            formatter: (checked: boolean) => checked ? 'Yes' : 'No',
            widget: 'checkbox'
          },
          {
            label: 'Is Under Evaluation',
            name: 'isEvaluating',
            formatter: (checked: boolean) => checked ? 'Yes' : 'No',
            widget: 'checkbox'
          },
          {
            label: 'Created At',
            formatter: (datestr: string) => date2str(datestr, 'DD MMM YYYY, HH:mm'),
            name: 'created',
            readonly: true,
            widget: 'text'
          },
          {
            label: 'Last Updated At',
            name: 'updated',
            formatter: (datestr: string) => date2str(datestr, 'DD MMM YYYY, HH:mm'),
            readonly: true,
            widget: 'text'
          },
        ]
      }),
    },
    {
      id: 'users',
      title: 'Users',
      hide: () => !CurrentUser.getInstance().canManageResource(EFeature.user),
      render: (organisation: OrganisationDto) => {
        const userColumns = generateUserColumns(
          organisation,
          {
            isAdmin: CurrentUser.getInstance().isAdmin()
          },
          {
            handleDisableUser,
            handleEnableUser,
          }
        );
        return (
          <div>
            <ResourceTable
              columns={userColumns}
              onFetch={GetUsers}
              defaultQuery={[{
                column: 'organisationId',
                value: id
              }]}
            />
          </div>
        );
      },
      actions: (organisation) => {
        const isActive = organisation.status === 'Active';
        const msg: string = isActive
          ? 'Diable this organisation will block all its users from signing in Kosmos.'
          : 'Enable this organisation will unblock all its users to sign in Kosmos';
        return [
          {
            label: 'Create user',
            onClick: () => setOpenCreateUser(true),
            disabled: !isActive,
          },
          {
            label: isActive ? 'Disable Organisation' : 'Enable Organisation',
            color: isActive ? 'error' : 'primary',
            onClick: isActive ? handleDisableOrganisation : handleEnableOrganisation,
            hide: () => !CurrentUser.getInstance().isAdmin(),
            confirmMessage: msg,
          },
        ];
      }
    },
    {
      id: 'companyLists',
      title: 'Company List',
      hide: () => !CurrentUser.getInstance().canManageResource(EFeature.companyList),
      render: () => {
        const companyListColumns = generateCompanyListColumns();
        return (
          <div>
            <ResourceTable
              columns={companyListColumns}
              onFetch={GetCompanyLists}
              defaultQuery={[{
                column: 'organisationId',
                value: id
              }]}
            />
          </div>
        );
      },
      actions: (organisation) => {
        const isActive = organisation.status === 'Active';
        return [
          {
            label: 'Create new company list',
            onClick: () => setOpenCreateCompanyList(true),
            disabled: !isActive,
          },
        ];
      }
    },
    {
      id: 'activities',
      title: 'Activities',
      render: () => {
        return (
          <div>
            <ResourceTable
              filterSchema={auditLogFilterSchema}
              columns={generateActivityColumns()}
              onFetch={GetPaginatedAuditLogs}
              defaultQuery={[{
                column: 'organisationId',
                value: id
              }]}
              defaultSort={{
                sortColumn: 'created',
                isDesc: true,
              }}
            />
          </div>
        );
      },
    },
    {
      id: 'auditLogSummary',
      title: 'Audit Dashboard',
      render: () => {
        return (
          <div>
            <ResourceFullListTable
              onFiltersChange={(filters) => setAuditFilters(filters)}
              filterSchema={auditLogFilterSchema}
              columns={generateAuditLogSummaryColumns()}
              onFetch={GetAuditLogSummary}
              defaultQuery={[{
                column: 'organisationId',
                value: id
              }]}
            />
          </div>
        );
      },
      actions: () => {
        return [
          {
            label: 'Download billing report',
            onClick: handleDownloadBillingReport,
          },
        ];
      }
    },
    {
      id: 'scoredCompanies',
      title: 'Scored Companies',
      render: () => {
        return (
          <div>
            <ScoredCompaniesResourceTable
              filterSchema={scoredCompaniesFilterSchema}
              columns={generateScoredCompaniesColumns(id)}
              onFetch={GetListScoredCompanies}
              defaultQuery={[{
                column: 'organisationId',
                value: id
              }]}
            />
          </div>
        );
      },
    },
    {
      id: 'credits',
      title: 'Credits/Subscriptions',
      hide: () => {
        return !CurrentUser.getInstance().isAdmin();
      },
      render: (organisation: OrganisationDto) => {
        return (
          <div>
            <CreditUpdateWidget
              organisationId={id}
              remainingCredits={organisation.remainingCredits || 0}
              thresholdCredits={organisation.thresholdCredits || 0}
              onUpdateOrganisation={() => setNonce(generateNonce())}
            />
          </div>
        );
      },
    },
  ];

  const handleFetch = async (): Promise<OrganisationDto> => {
    return await GetOrganisation(id);
  };

  const handleUpdate = async (attrs: { name?: string, display?: string }): Promise<OrganisationDto> => {
    return await UpdateOrganisation(id, attrs);
  };

  const handleError = (e: unknown) => {
    if (axios.isAxiosError(e) && e.response) {
      // console.log(e.response?.data);
      const errorMessage = get(e.response?.data, 'message') || 'Something went wrong';
      setOpenNotice(true);
      setNoticeData({
        message: errorMessage,
        severity: 'error',
      });
    } else {
      // native error
      console.error(e);
      setOpenNotice(true);
      setNoticeData({
        message: e instanceof Error ? e.message : String(e),
        severity: 'error',
      });
    }
  };

  const handleCreateUser = async (attrs: UserAttributes) => {
    const injectOrg = {
      ...attrs,
      organisationId: id,
    };
    try {
      const createdMessage = await CreateUser(injectOrg);
      setNonce(generateNonce);
      setOpenCreateUser(false);
      setOpenNotice(true);
      setNoticeData({
        message: createdMessage || 'Success',
        severity: 'success',
      });
    } catch (e) {
      handleError(e);
    }
  };

  const handleCreateCompanyList = async (attrs: CompanyListAttributes) => {
    const injectOrg = {
      ...attrs,
      organisationId: id,
    };
    try {
      await CreateCompanyList(injectOrg);
      setNonce(generateNonce());
      setOpenCreateCompanyList(false);
      setOpenNotice(true);
      setNoticeData({
        message: 'Success!',
        severity: 'success',
      });
    } catch (e) {
      handleError(e);
    }
  };

  return (
    <Root>
      <ResourceDetailContainer
        key={nonce}
        tabs={generateTabs()}
        breadcrumbText="Organisations"
        breadcrumbUrl="/organisations"
        onTabChanged={setTabId}
        onFetch={handleFetch}
        onUpdate={handleUpdate}
        currentTabId={tabId}
      />
      <CreateResourceModal<UserAttributes>
        open={openCreateUser}
        label="Create a new user"
        schema={userCreateSchema}
        onClose={() => setOpenCreateUser(false)}
        onSave={handleCreateUser}
      />
      <CreateResourceModal<CompanyListAttributes>
        open={openCreateCompanyList}
        label="Create a new company list"
        schema={companyListCreateSchema}
        onClose={() => setOpenCreateCompanyList(false)}
        onSave={handleCreateCompanyList}
      />
      {noticeData && (
        <Notice
          open={openNotice}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseCreatedNotice}
        />
      )}
    </Root>
  );
}

export default OrganisationDetailPage;
